import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import { SuggestionData } from 'ckeditor5-premium-features'
import clsx from 'clsx'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles/useOptionGroupStyles'
import React, { useMemo } from 'react'
import { extractSuggestions } from '../ContentEditor/contentUtils'

/**
 * Determines the appropriate pill style class based on contentItem state.
 */
const getPillStyle = (
    contentItem: Partial<TextContentElement>,
    isInactive: boolean,
    classNames: Record<string, string>
) => {
    if (isInactive || contentItem.optionItemState === 'InActive')
        return classNames.inactivatedOptionItem
    if (contentItem.optionItemState === 'OptedIn') {
        return contentItem.isPremiumPlus
            ? classNames.moSelectedOption
            : classNames.selectedOptionItem
    }
    return contentItem.isPremiumPlus
        ? classNames.moUnselectedOption
        : classNames.unselectedOptionItem
}

/** Props for the SelectionOption component. */
export interface SelectionOptionProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** The label to display */
    label?: string
    /** The classes to apply */
    classes?: any
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
}

export const SelectionOption = React.memo<SelectionOptionProps>(
    ({ contentItem, label, classes, inactive = false, editable = false }) => {
        const classNames = useOptionGroupStyles()
        const pillStyle = useMemo(
            () => getPillStyle(contentItem, inactive, classNames),
            [contentItem, inactive, classNames]
        )
        const initialSuggestions: SuggestionData[] = useMemo(
            () => extractSuggestions([contentItem]),
            [contentItem.id, contentItem.changeType]
        )

        return (
            <Typography
                key={`option-text-${contentItem.id || 'default'}`}
                data-testid={`option-text-${contentItem.id || 'default'}`}
                className={classes || clsx(classNames.optionItem, pillStyle)}
                data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
            >
                {label || contentItem.text || ''}
            </Typography>
        )
    }
)
